import stages from '../constants/stages';

const VALID_STAGES = new Set(Object.values(stages));

const validateStage = (stage) => {
  if (!VALID_STAGES.has(stage)) {
    // eslint-disable-next-line no-console
    console.warn(
      `Invalid stage: ${stage?.toString() || stage}. Valid stages: ${Object.keys(stages).join(
        ', '
      )}`
    );

    return false;
  }

  return true;
};

const validateHandler = (handlerFn) => {
  if (handlerFn === null) {
    // eslint-disable-next-line no-console
    console.warn('validateHandler: handler cannot be null');

    return false;
  }

  if (typeof handlerFn !== 'function') {
    // eslint-disable-next-line no-console
    console.warn(`validateHandler: handler must be a function, got ${typeof handlerFn}`);
    return false;
  }

  return true;
};

const createStageHandlersRegistry = (initialStageHandlers) => {
  const stageHandlers = initialStageHandlers ?? new Map();

  const register = (chunkId, stage, handlerFn) => {
    if (!chunkId || !stage) {
      // eslint-disable-next-line no-console
      console.warn(
        `register: chunkId and stage are required. Got chunkId: ${chunkId}, stage: ${stage}`
      );

      return false;
    }

    if (!validateStage(stage)) {
      return false;
    }

    if (!validateHandler(handlerFn)) {
      return false;
    }

    if (!stageHandlers.has(stage)) {
      stageHandlers.set(stage, new Map());
    }

    const handlersForStage = stageHandlers.get(stage);

    if (!handlersForStage.has(chunkId)) {
      handlersForStage.set(chunkId, []);
    }

    const existingHandlers = handlersForStage.get(chunkId);

    existingHandlers.push(handlerFn);

    return true;
  };

  const registerStageHandlersForChunk = (chunkId, stageHandlersConfig) => {
    if (!chunkId) {
      // eslint-disable-next-line no-console
      console.warn('registerStageHandlersForChunk: chunkId is required');
      return;
    }

    if (!stageHandlersConfig) {
      // eslint-disable-next-line no-console
      console.warn('registerStageHandlersForChunk: stageHandlersConfig is required');
      return;
    }

    if (!Array.isArray(stageHandlersConfig)) {
      // eslint-disable-next-line no-console
      console.warn(
        `registerStageHandlersForChunk: stageHandlersConfig must be an array, got ${typeof stageHandlersConfig}`
      );
      return;
    }

    stageHandlersConfig.forEach((stageHandlerConfig, index) => {
      if (!stageHandlerConfig?.stage) {
        // eslint-disable-next-line no-console
        console.warn(
          `registerStageHandlersForChunk: stageHandlerConfig[${index}] is missing stage property. Config:`,
          stageHandlerConfig
        );

        return;
      }

      const { stage, handler: handlerFn } = stageHandlerConfig;

      register(chunkId, stage, handlerFn);
    });
  };

  const unregisterStageHandlersForChunk = (chunkId) => {
    if (!chunkId) {
      // eslint-disable-next-line no-console
      console.warn('unregisterStageHandlersForChunk: chunkId is required');
      return;
    }

    stageHandlers.forEach((handlersForStage, stage) => {
      if (handlersForStage.has(chunkId)) {
        handlersForStage.delete(chunkId);

        if (handlersForStage.size === 0) {
          stageHandlers.delete(stage);
        }
      }
    });
  };

  const getStageHandlers = () => stageHandlers;

  const getHandlersByStage = (stage) => {
    const handlersForStage = stageHandlers.get(stage);

    if (!handlersForStage || handlersForStage.size === 0) {
      return [];
    }

    const result = [];

    handlersForStage.forEach((chunkHandlers, chunkId) => {
      const validHandlers = chunkHandlers.filter(validateHandler);
      const invalidCount = chunkHandlers.length - validHandlers.length;

      if (invalidCount > 0) {
        // eslint-disable-next-line no-console
        console.warn(
          `getHandlersByStage: filtered out ${invalidCount} invalid handler(s) for chunkId "${chunkId}" at stage ${String(
            stage
          )}`
        );
      }

      validHandlers.forEach((handler) => {
        result.push({ handler, chunkId });
      });
    });

    return result;
  };

  return {
    registerStageHandlersForChunk,
    unregisterStageHandlersForChunk,
    getStageHandlers,
    getHandlersByStage,
  };
};

export default createStageHandlersRegistry;
